import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  IconButton,
  Breadcrumbs,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
  Paper
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import DirectionsIcon from "@mui/icons-material/Directions";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MaleProfilePhoto from "../../../../../assets/images/male-doc.png";
import FemaleProfilePhoto from "../../../../../assets/images/female-doc.webp";
import { styled } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import { useQuery } from "@tanstack/react-query";
import { MemberService } from "../../../../../network/MemberService";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

interface Institution {
  _id?: string;
  name: string;
  specialty?: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface Member {
  _id: string;
  name: string;
  surname: string;
  title?: string;
  image?: string;
  gender?: string;
  memberNumber?: string;
  department?: string;
  birthDate?: string;
  city?: string;
  email?: string;
  phoneNumber?: string;
  bio?: string;
  institutions: Institution[];
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  boards?: {
    management?: {
      isSelected: boolean;
      role: string;
      position?: string;
      specialty?: string;
      email?: string;
      phone?: string;
      address?: string;
    };
    disciplinary?: {
      isSelected: boolean;
      role: string;
      position?: string;
      specialty?: string;
      email?: string;
      phone?: string;
      address?: string;
    };
    audit?: {
      isSelected: boolean;
      role: string;
      position?: string;
      specialty?: string;
      email?: string;
      phone?: string;
      address?: string;
    };
  };
}

const getBoardRole = (member: any) => {
  try {
    if (member?.boards?.management?.isSelected && member.boards.management.position) {
      return ` ${member.boards.management.position}`;
    }
    return "";
  } catch (error) {
    console.error("Error getting board role:", error);
    return "";
  }
};

const CityBox = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--red-primary)",
  color: "#fff",
  padding: theme.spacing(1),
  top: "0",
  textAlign: "center",
  borderRadius: "0 0 16px 16px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5)
}));

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const renderBoardDetails = (boards: any) => {
  if (!boards) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, color: 'var(--red-primary)' }}>
        Kurul Üyelikleri
      </Typography>

      <Grid container spacing={2}>
        {Object.entries(boards).map(([boardType, boardData]: [string, any]) => {
          if (!boardData.isSelected) return null;

          return (
            <Grid item xs={12} md={4} key={boardType}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 2,
                  height: '100%',
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--red-primary)'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: 'var(--red-primary)',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  {boardType === 'management' ? 'Yönetim Kurulu' :
                   boardType === 'disciplinary' ? 'Disiplin Kurulu' : 
                   'Denetim Kurulu'}
                </Typography>

                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Rol
                    </Typography>
                    <Typography>
                      {boardData.role === 'MAIN_MEMBER' ? 'Asıl Üye' : 'Yedek Üye'}
                    </Typography>
                  </Box>

                  {boardData.specialty && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Branş
                      </Typography>
                      <Typography>{boardData.specialty}</Typography>
                    </Box>
                  )}

                  {boardData.position && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Görev
                      </Typography>
                      <Typography>{boardData.position}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const ProfileCard = styled(Stack)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  transition: 'transform 0.3s ease-in-out',
  cursor:"pointer"
}));

const DetailCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff', 
  borderRadius: '16px',
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    width: '100%'
  }
}));

const InstitutionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  transition: 'all 0.3s ease',
  cursor: 'default',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    boxShadow: 'none',
    border: 'none'
  },
  '&:hover': {
    transform: 'translateY(-1px)',
      cursor:"pointer"
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'var(--red-primary)',
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'var(--red-secondary)',
    transform: 'translateY(-2px)',
      cursor:"pointer"
  }
})) as typeof IconButton;

const DirectionsButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: 'var(--red-primary)',
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'var(--red-secondary)',
    transform: 'translateY(-1px)',
      cursor:"pointer"
  }
})) as typeof Button;

const StyledBreadcrumb = styled(Link)({
  color: "var(--gray-color)",
  textDecoration: 'none',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: 'var(--red-primary)',
      cursor:"pointer"
  }
});

function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const memberService = new MemberService();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    data: memberDetail,
    isError,
    isLoading,
  } = useQuery<Member>({
    queryKey: ["memberDetail", id],
    queryFn: async () => {
      const res = await memberService.getById(id!);
      console.log('API Response:', res);
      console.log('Member Data:', res?.data);
      console.log('Institutions Data:', res?.data?.institutions);
      
      if (res?.data?.institutions) {
        res.data.institutions.forEach((inst: Institution, index: number) => {
          console.log(`Institution ${index + 1} Details:`, {
            name: inst.name,
            address: inst.address,
            addressType: typeof inst.address,
            hasAddress: 'address' in inst,
            allFields: inst
          });
        });
      }
      return res?.data;
    },
  });

  const socialMedia = [
    { icon: LinkedInIcon, link: memberDetail?.socialMedia?.linkedin },
    { icon: TwitterIcon, link: memberDetail?.socialMedia?.twitter },
    { icon: FacebookIcon, link: memberDetail?.socialMedia?.facebook },
    { icon: InstagramIcon, link: memberDetail?.socialMedia?.instagram },
    { icon: YouTubeIcon, link: memberDetail?.socialMedia?.youtube }
  ];

  if (isError) {
    return <div>Böyle bir üye bulunamadı</div>;
  }

  return (
    <section style={{
      backgroundColor: "#ffffff", 
      minHeight: "100vh",
      padding: isMobile ? "20px" : "50px",
      margin: isMobile ? " -20px " : "50px",
      marginTop: "-20px",            
    }}>
      {!isLoading && (
        <div className="container">
          <Grid container spacing={isMobile ? 1 : 4} mt={1}>
            <Grid item xs={12}>
              <Breadcrumbs 
                aria-label="breadcrumb"
                sx={{
                  '.MuiBreadcrumbs-ol': {
                    flexWrap: 'wrap'
                  }
                }}
              >
                <StyledBreadcrumb to="/">Ana Sayfa</StyledBreadcrumb>
                <StyledBreadcrumb to="/uyeler">Üyelerimiz</StyledBreadcrumb>
                <Typography color="var(--red-primary)">{memberDetail.name} {memberDetail.surname}</Typography>
              </Breadcrumbs>
            </Grid>

            <Grid item xs={12} md={4}>
              <ProfileCard>
                {memberDetail?.boards?.management?.isSelected === true && (
                  <Box
                    position="absolute"
                    top={8}
                    right={8}
                    bgcolor="rgba(255, 255, 255, 0.8)"
                    borderRadius="50%"
                    padding="4px"
                  >
                    <Tooltip title={"Yönetim Kurulu Üyesi"}>
                      <StarRateRoundedIcon style={{ color: "silver" }} />
                    </Tooltip>
                  </Box>
                )}
                <img
                  alt={memberDetail.name}
                  src={
                    memberDetail.image ||
                    (memberDetail.gender?.toLowerCase() === "male"
                      ? MaleProfilePhoto
                      : FemaleProfilePhoto)
                  }
                  style={{
                    width: "100%",
                    height: isMobile ? "300px" : "450px",
                    borderRadius: "16px 16px 0 0",
                    objectFit: "cover",
                  }}
                />
                <CityBox>
                  {getBoardRole(memberDetail) && (
                    <Typography className="B-18" style={{ color: "var(--white-color)" }}>
                      {getBoardRole(memberDetail)}
                    </Typography>
                  )}
                  {memberDetail.city && (
                    <Typography className="B-16" style={{ color: "var(--white-color)" }}>
                      {memberDetail.city}
                    </Typography>
                  )}
                </CityBox>
              </ProfileCard>
            </Grid>

            <Grid item xs={12} md={8}>
              <DetailCard>
                <Stack spacing={3} alignItems="flex-start" sx={{ width: '100%' }}>
                  {/* İsim Başlığı */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: "var(--red-primary)",
                      fontWeight: 700,
                      mb: 2
                    }}
                  >
                    {memberDetail.title} {memberDetail.name} {memberDetail.surname}
                  </Typography>

                  {/* Kişisel Bilgiler */}
                  <Box sx={{ width: '100%' }}>
                    <Grid container spacing={2}>
                      {memberDetail.department && (
                        <Grid 
                          item 
                          xs={12} 
                          sm={6}
                          sx={{
                            display: 'flex',
                            justifyContent: isMobile ? 'center' : 'flex-start',
                            width: '100%'
                          }}
                        >
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={1}
                            sx={{
                              width: isMobile ? '100%' : 'auto',
                              justifyContent: isMobile ? 'center' : 'flex-start'
                            }}
                          >
                            <Typography 
                              variant="h5"
                              sx={{
                                textAlign: isMobile ? 'center' : 'left',
                                fontSize: isMobile ? '1.25rem' : 'h5.fontSize',
                                width: isMobile ? '100%' : 'auto'
                              }}
                            >
                              {memberDetail.department}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Kurumlar */}
                  {memberDetail.institutions?.length > 0 && (
                    <Box sx={{ width: '100%' }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          color: 'var(--red-primary)',
                          position: 'relative',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -4,
                            left: 0,
                            width: 40,
                            height: 2,
                            backgroundColor: 'var(--red-primary)'
                          }
                        }}
                      >
                        Kurumlar
                      </Typography>
                      <Grid container spacing={2}>
                        {memberDetail.institutions.map((institution: Institution, index: number) => (
                          <Grid item xs={12} key={index}>
                            <InstitutionCard>
                              {/* Kurum Adı ve branş*/}
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {institution.name}   {institution.specialty} 
                                </Typography>
                              </Box>

                              {/* İletişim Bilgileri */}
                              <Stack spacing={1}>
                                {/* Email */}
                                {institution.email && (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <EmailIcon sx={{ color: 'var(--red-primary)', fontSize: '0.9rem' }} />
                                    <Typography variant="body2">{institution.email}</Typography>
                                  </Box>
                                )}
                                
                                {/* Telefon */}
                                {institution.phone && (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <PhoneIcon sx={{ color: 'var(--red-primary)', fontSize: '0.9rem' }} />
                                    <Typography variant="body2">{institution.phone}</Typography>
                                  </Box>
                                )}

                                {/* Adres */}
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                  <LocationOnIcon sx={{ color: 'var(--red-primary)', fontSize: '0.9rem', mt: 0.5 }} />
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      {institution.address ? institution.address : 'Adres bilgisi girilmemiş'}
                                    </Typography>
                                    {institution.address && (
                                      <DirectionsButton
                                        variant="contained"
                                        startIcon={<DirectionsIcon />}
                                        size="small"
                                        component="a" 
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(institution.address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer" 
                                      >
                                        Yol Tarifi
                                      </DirectionsButton>
                                    )}
                                  </Box>
                                </Box>
                              </Stack>
                            </InstitutionCard>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Biyografi */}
                  {memberDetail.bio && (
                    <Box sx={{ width: '100%' }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          color: 'var(--red-primary)',
                          position: 'relative',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -4,
                            left: 0,
                            width: 40,
                            height: 2,
                            backgroundColor: 'var(--red-primary)'
                          }
                        }}
                      >
                        Biyografi
                      </Typography>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 2,
                          cursor: 'pointer', 
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          },
                          [theme.breakpoints.down('sm')]: {
                            boxShadow: 'none',
                            border: 'none',
                            padding: 0
                          }
                        }}
                      >
                        <Typography>{memberDetail.bio}</Typography>
                      </Paper>
                    </Box>
                  )}

                  <Box sx={{ width: '100%', mt: 'auto', pt: 2 }}>
                    <Stack 
                      direction={isMobile ? 'column' : 'row'} 
                      spacing={2} 
                      alignItems={isMobile ? 'flex-start' : 'center'}
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      {memberDetail?.boards?.management?.isSelected && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            order: 1,
                            mb: isMobile ? 2 : 0,
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          <StarRateRoundedIcon 
                            sx={{ 
                              color: "#000000",
                              fontSize: isMobile ? '22px' : '26px',
                              fontWeight: 'bold'
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#000000',
                              fontWeight: 800,
                              fontSize: isMobile ? '14px' : '16px',
                              letterSpacing: '0.2px',
                              cursor:"pointer"
                            }}
                          >
                            BJK Yönetim Kurulu Üyesi
                          </Typography>
                        </Box>
                      )}

                      <Stack 
                        direction="row" 
                        spacing={1.5} 
                        sx={{ 
                          order: isMobile ? 2 : 2,
                          justifyContent: isMobile ? 'flex-start' : 'flex-end',
                          width: isMobile ? '100%' : 'auto'
                        }}
                      >
                        {socialMedia.map((media, index) => 
                          media.link ? (
                            <SocialButton
                              key={index}
                              component="a"
                              href={media.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              size={isMobile ? "small" : "medium"}
                            >
                              <media.icon />
                            </SocialButton>
                          ) : null
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </DetailCard>
            </Grid>
          </Grid>
        </div>
      )}
    </section>
  );
}

export default MemberDetail;